import { PLATFORM_ID, Provider } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
  BrowserAuthOptions,
  stubbedPublicClientApplication,
} from '@azure/msal-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AppConfigService } from '../services/app-config.service';

export function loggerCallback(_logLevel: LogLevel, message: string): void {
  // MSAL diagnostic hook — only surface in non-production builds.
  if (!environment.production) {
    // eslint-disable-next-line no-console
    console.log(message);
  }
}

export function MSALInstanceFactory(
  config: AppConfigService,
  platformId: object
): IPublicClientApplication {
  // MSAL is browser-only — return a stub during SSR
  if (!isPlatformBrowser(platformId)) {
    return stubbedPublicClientApplication;
  }

  const msal = config.config['msal'] as Record<string, BrowserAuthOptions>;
  let msalAuth: BrowserAuthOptions | undefined;

  if (msal) {
    msalAuth = msal['auth'];
  } else {
    console.error('Configure msal in ' + AppConfigService.configPath);
  }

  return new PublicClientApplication({
    auth: msalAuth as BrowserAuthOptions,
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(
  config: AppConfigService
): MsalInterceptorConfiguration {
  const msal = config.config['msal'] as Record<
    string,
    Record<string, Record<string, Iterable<readonly [unknown, unknown]> | unknown>>
  >;

  const protectedResourceMap = msal?.['auth']?.['framework']?.['protectedResourceMap']
    ? new Map(msal['auth']['framework']['protectedResourceMap'] as Iterable<readonly [string, string[]]>)
    : new Map<string, string[]>();

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/login-failed',
  };
}

interface IProviderMap {
  [key: string]: Provider[];
}

export const MSAL_PROVIDERS: IProviderMap = {
  mock: [
    { provide: MSAL_GUARD_CONFIG, useValue: { client: 'mock', authority: 'mock' } },
    { provide: MSAL_INTERCEPTOR_CONFIG, useValue: {} },
    { provide: MSAL_INSTANCE, useValue: stubbedPublicClientApplication },
    MsalService,
  ],
  app: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [AppConfigService, PLATFORM_ID],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [AppConfigService],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
};

export function getMsalProviders(): Provider[] {
  return MSAL_PROVIDERS[environment.providers] ?? MSAL_PROVIDERS['app'];
}

export function emptyGuard(
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): boolean {
  return true;
}
